// Libraries
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { ActionService } from 'src/app/services/api/actions/action.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UserService } from 'src/app/services/api/users/user.service';

// Interfaces
import { Action, ActionPriority, ActionStatus, ActionType } from 'src/app/interfaces/api/action';
import { defaultProject, Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';

// Classes
import { Pop } from 'src/app/classes/shared/pop';
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';

// Components
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { DeleteModalComponent } from 'src/app/components/shared/dialogs/delete-modal/delete-modal.component';
import { ButtonComponent, ChipComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';
import { ActionDialogComponent } from './dialogs/action-dialog/action-dialog.component';

@Component({
  selector: 'app-actions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    ChipComponent,
    MatMenuModule,
    RouterLink,
    TranslateModule,
    TimeFormatPipe,
    ButtonComponent,
    SearchBarComponent,
    SpinnerComponent,
    NoDataComponent,
  ],
  templateUrl: './actions.component.html',
  styleUrls: ['./actions.component.scss'],
})
export class ActionsComponent implements AfterViewInit, OnDestroy {
  private readonly actionService: ActionService = inject(ActionService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly userService: UserService = inject(UserService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly pop: Pop = inject(Pop);

  @ViewChild('actionPaginator', { static: false }) actionPaginator: MatPaginator = new MatPaginator();
  @ViewChild(MatSort) sort: MatSort = new MatSort();

  loading: boolean = false;
  actions: Action[] = [];
  usersById: Record<string, User> = {};
  actionsCount: number = 0;
  actionsData: MatTableDataSource<Action> = new MatTableDataSource();
  actionsColumns: string[] = ['title', 'type', 'priority', 'status', 'assignee', 'dueDate', 'risk', 'actions'];
  project: Project = defaultProject;
  destroy$ = new Subject<void>();
  searchText: string = '';

  filterStatus: ActionStatus | '' = '';
  filterType: ActionType | '' = '';
  filterPriority: ActionPriority | '' = '';

  statusOptions: ActionStatus[] = ['todo', 'in_progress', 'done', 'closed', 'cancelled'];
  typeOptions: ActionType[] = ['corrective', 'preventive', 'improvement'];
  priorityOptions: ActionPriority[] = ['low', 'medium', 'high', 'critical'];

  constructor() {
    this.projectService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(project => {
      const changed = this.project && this.project.id !== project.id;
      this.project = project;
      if (changed) {
        this.loadUsers();
        this.loadActions();
      }
    });
  }

  private loadUsers() {
    if (!this.project?.id) return;
    this.userService.getUsersLight(this.project.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (users: User[]) => {
        this.usersById = {};
        (users || []).forEach(u => { if (u?.id) this.usersById[u.id] = u; });
      },
      error: () => { this.usersById = {}; },
    });
  }

  assigneeLabel(action: Action): string {
    const a: any = action.assignee;
    // assignee may be a populated User object or a plain id string
    if (a && typeof a === 'object') {
      const name = [a.firstName, a.lastName].filter(Boolean).join(' ').trim();
      return name || a.email || action.assigneeName || '—';
    }
    const user = a ? this.usersById[a] : null;
    if (user) {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      return name || user.email || action.assigneeName || '—';
    }
    return action.assigneeName || '—';
  }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => {
      const riskId = this.route.snapshot.queryParamMap.get('risk');
      const openAdd = this.route.snapshot.queryParamMap.get('add') === 'true';
      const editId = this.route.snapshot.queryParamMap.get('edit');
      this.loadUsers();
      this.loadActions(() => {
        if (editId) this.openById(editId);
      });
      if (openAdd) this.add(riskId || undefined);
    });
  }

  private openById(id: string) {
    const action = this.actions.find(a => a.id === id);
    if (action) this.edit(action);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadActions(onReady?: () => void) {
    this.loading = true;

    const query: any = { populated: true };
    if (this.project?.id) query.project = this.project.id;

    this.actionService.getActions(query).pipe(takeUntil(this.destroy$)).subscribe({
      next: (actions) => {
        this.actions = actions || [];
        console.log('Loaded actions:', this.actions);
        this.applyFilters();
        this.loading = false;
        if (onReady) onReady();
      },
      error: (err) => {
        console.error(err);
        this.actions = [];
        this.applyFilters();
        this.loading = false;
      },
    });
  }

  applyFilters() {
    let filtered = this.actions;
    if (this.filterStatus) filtered = filtered.filter(a => a.status === this.filterStatus);
    if (this.filterType) filtered = filtered.filter(a => a.type === this.filterType);
    if (this.filterPriority) filtered = filtered.filter(a => a.priority === this.filterPriority);

    this.actionsCount = filtered.length;
    this.actionsData = new MatTableDataSource(filtered);
    this.actionsData.sort = this.sort;
    this.actionsData.paginator = this.actionPaginator;
    if (this.searchText) this.actionsData.filter = this.searchText.trim().toLowerCase();
  }

  search(text: string = '') {
    if (text !== this.searchText) {
      this.searchText = text;
      if (this.actionPaginator) this.actionPaginator.pageIndex = 0;
      this.actionsData.filter = this.searchText.trim().toLowerCase();
    }
  }

  add(riskId?: string) {
    const modal = this.dialog.open(ActionDialogComponent, {
      width: '720px',
      data: {
        mode: 'add',
        action: { source: riskId ? 'risk' : 'manual', risks: riskId ? [riskId] : [] },
        projectId: this.project?.id,
      },
    });

    modal.afterClosed().subscribe(res => {
      if (res) this.loadActions();
    });
  }

  edit(action: Action) {
    const modal = this.dialog.open(ActionDialogComponent, {
      width: '720px',
      data: {
        mode: 'edit',
        action: { ...action },
        projectId: this.project?.id,
      },
    });

    modal.afterClosed().subscribe(res => {
      if (res) this.loadActions();
    });
  }

  remove(action: Action) {
    const dialogRef = this.dialog.open(DeleteModalComponent, {
      data: {
        title: this.translate.instant('actions.title_one'),
        name: action.title,
      },
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this.actionService.removeAction(action.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.actions = this.actions.filter(a => a.id !== action.id);
            this.applyFilters();
            this.pop.toast(this.translate.instant('actions.delete_success'), 'success', 4000, 'delete');
          },
          error: (err) => {
            console.error(err);
            this.pop.error(this.translate.instant('actions.delete_error'));
          },
        });
      }
    });
  }

  exportCSV() {
    const rows = this.actionsData.filteredData?.length ? this.actionsData.filteredData : this.actions;
    if (!rows.length) return;

    const t = (key: string) => this.translate.instant(key);
    const headers = [
      t('actions.field.title'),
      t('actions.field.type'),
      t('actions.field.priority'),
      t('actions.field.status'),
      t('actions.field.assignee'),
      t('actions.field.dueDate'),
      t('actions.field.risk'),
    ];

    const escape = (value: any): string => {
      const s = (value ?? '').toString();
      return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const formatDate = (d?: string) => {
      if (!d) return '';
      const date = new Date(d);
      return isNaN(date.getTime()) ? '' : date.toLocaleDateString('fr-FR');
    };

    const lines = [headers.join(';')];
    rows.forEach(a => {
      const assignee = this.assigneeLabel(a);
      const risks = (a.risks || [])
        .map((r: any) => r?.payload?.displayTitle || r?.payload?.title || r?.id || '')
        .filter(Boolean)
        .join(' | ');
      lines.push([
        a.title,
        t('actions.type.' + a.type),
        t('actions.priority.' + a.priority),
        t('actions.status.' + a.status),
        assignee === '—' ? '' : assignee,
        formatDate(a.dueDate),
        risks,
      ].map(escape).join(';'));
    });

    const bom = '﻿';
    const blob = new Blob([bom + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    link.setAttribute('download', `actions-${stamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  statusChipColor(status: ActionStatus): string {
    switch (status) {
      case 'todo': return 'warn';
      case 'in_progress': return 'accent';
      case 'done': return 'primary';
      case 'closed': return 'primary';
      case 'cancelled': return 'muted';
      default: return '';
    }
  }

  priorityChipColor(priority: ActionPriority): string {
    switch (priority) {
      case 'critical': return 'danger';
      case 'high': return 'warn';
      case 'medium': return 'accent';
      case 'low': return 'muted';
      default: return '';
    }
  }
}
